import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Box, Button, Chip, IconButton, TextField, Typography } from '@weave-mui/material';
const REPO_FULL_NAME_RE = /^[\w.-]+\/[\w.-]+$/;
const SLACK_USER_GROUP_ID_RE = /^S[A-Z0-9]{8,}$/;
/** Groups repos that mention the exact same user groups back into one rule, in stored order. */
export function reviewersToRules(reviewers) {
    const byGroups = new Map();
    for (const [repo, groupIds] of Object.entries(reviewers ?? {})) {
        const key = [...groupIds].sort().join(',');
        const rule = byGroups.get(key);
        if (rule)
            rule.repos.push(repo);
        else
            byGroups.set(key, { repos: [repo], groupIds: [...groupIds] });
    }
    return Array.from(byGroups.values());
}
function isBlank(rule) {
    return rule.repos.length === 0 && rule.groupIds.length === 0;
}
export function ruleErrors(rule, rules) {
    if (isBlank(rule))
        return {};
    const errors = {};
    const badRepo = rule.repos.find((r) => !REPO_FULL_NAME_RE.test(r));
    const otherRepos = new Set(rules.filter((r) => r !== rule).flatMap((r) => r.repos.map((x) => x.toLowerCase())));
    const dupRepo = rule.repos.find((r) => otherRepos.has(r.toLowerCase()));
    if (rule.repos.length === 0)
        errors.repos = 'Add at least one repo';
    else if (badRepo)
        errors.repos = `"${badRepo}" isn't "org/repo"`;
    else if (dupRepo)
        errors.repos = `"${dupRepo}" is already in another rule`;
    const badId = rule.groupIds.find((id) => !SLACK_USER_GROUP_ID_RE.test(id));
    if (rule.groupIds.length === 0)
        errors.groupIds = 'Add at least one user group';
    else if (badId)
        errors.groupIds = `"${badId}" isn't a user group ID (e.g. S035EHL63CH)`;
    return errors;
}
/** `null` while any non-blank rule is invalid, so callers can block saving until it's fixed. */
export function rulesToReviewers(rules) {
    const filled = rules.filter((r) => !isBlank(r));
    if (filled.some((r) => Object.keys(ruleErrors(r, filled)).length > 0))
        return null;
    return Object.fromEntries(filled.flatMap((r) => r.repos.map((repo) => [repo, r.groupIds])));
}
/** Free-text chip input: Enter, comma, or space commits a chip (so pasted lists split), and
 * blur commits whatever's left so a half-typed value isn't silently dropped on Save. */
function ChipInput({ label, placeholder, values, options = [], error, helperText, isValid, onChange }) {
    const [input, setInput] = useState('');
    function commit(text, rest = '') {
        const tokens = text.split(/[\s,]+/).filter(Boolean).filter((t) => !values.includes(t));
        if (tokens.length > 0)
            onChange([...values, ...tokens]);
        setInput(rest);
    }
    return (_jsx(Autocomplete, { multiple: true, freeSolo: true, size: "small", options: options.filter((o) => !values.includes(o)), value: values, inputValue: input, onChange: (_e, next) => onChange(next), onInputChange: (_e, text, reason) => {
            if (reason !== 'input')
                return setInput(text);
            const lastSep = Math.max(text.lastIndexOf(','), text.lastIndexOf(' '));
            if (lastSep >= 0)
                commit(text.slice(0, lastSep), text.slice(lastSep + 1));
            else
                setInput(text);
        }, renderTags: (tagValues, getTagProps) => tagValues.map((value, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (_jsx(Chip, { ...tagProps, size: "small", label: value, color: isValid(value) ? 'default' : 'error', sx: { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem' } }, key));
        }), renderInput: (params) => (_jsx(TextField, { ...params, label: label, placeholder: values.length === 0 ? placeholder : undefined, onBlur: () => commit(input), error: !!error, helperText: error ?? helperText })) }));
}
export default function ReviewersEditor({ rules, repoOptions, onChange }) {
    function updateRule(index, patch) {
        onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    }
    return (_jsxs(Box, { children: [_jsx(Typography, { variant: "caption", sx: { display: 'block', color: 'text.secondary', mb: 1 }, children: "Each rule mentions its Slack user groups at the end of every listed repo's nudge." }), rules.map((rule, index) => {
                const errors = ruleErrors(rule, rules);
                return (_jsxs(Box, { role: "group", "aria-label": `Reviewer rule ${index + 1}`, sx: { position: 'relative', p: 1.25, pr: 4, mb: 1, borderRadius: '8px', bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.08)' }, children: [_jsx(IconButton, { size: "small", "aria-label": `Remove reviewer rule ${index + 1}`, onClick: () => onChange(rules.filter((_r, i) => i !== index)), sx: { position: 'absolute', top: 4, right: 4, color: 'text.secondary' }, children: _jsx(CloseIcon, { sx: { fontSize: '0.95rem' } }) }), _jsx(ChipInput, { label: "Repos", placeholder: "org/repo", values: rule.repos, options: repoOptions, error: errors.repos, helperText: "Pick or type org/repo \u2014 add as many as share these reviewers", isValid: (v) => REPO_FULL_NAME_RE.test(v), onChange: (repos) => updateRule(index, { repos }) }), _jsx(ArrowDownwardIcon, { sx: { display: 'block', mx: 'auto', my: 0.25, fontSize: '0.9rem', color: 'text.disabled' } }), _jsx(ChipInput, { label: "Mention user groups", placeholder: "S035EHL63CH", values: rule.groupIds, error: errors.groupIds, helperText: "Slack user group IDs (S\u2026)", isValid: (v) => SLACK_USER_GROUP_ID_RE.test(v), onChange: (groupIds) => updateRule(index, { groupIds }) })] }, index));
            }), _jsx(Button, { variant: "outlined", size: "small", startIcon: _jsx(AddIcon, {}), onClick: () => onChange([...rules, { repos: [], groupIds: [] }]), sx: { textTransform: 'none', fontFamily: '"DM Sans", sans-serif', fontSize: '0.75rem' }, children: "Add reviewer rule" })] }));
}
